import React, { useEffect, useState } from "react";
import { FileUp, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "./LeadForm";

const FIELDS = ["customer_name", "company", "phone", "email", "city", "state", "product", "source", "inquiry", "notes"];
const SAMPLE = `customer_name,company,phone,email,city,state,product,source,inquiry
Ramesh Iyer,Coastal Surveys,+91 98200 11223,ramesh@coastalsurveys.in,Mumbai,Maharashtra,Falcon X7 Survey Drone,Exhibition,Needs 2 units for a port survey
Anita Bose,Everest Rentals,+91 90880 44557,anita@everestrentals.com,Kolkata,West Bengal,Robusto RC-4500 Hard Case,Website,Bulk enquiry for 25 cases`;

const parseCSV = (text) => {
  const lines = text.trim().split(/\r?\n/).filter(Boolean);
  if (lines.length < 2) return { rows: [], headers: [] };
  const split = (line) => {
    const out = []; let cur = ""; let q = false;
    for (const ch of line) {
      if (ch === '"') q = !q;
      else if (ch === "," && !q) { out.push(cur.trim()); cur = ""; }
      else cur += ch;
    }
    out.push(cur.trim());
    return out;
  };
  const headers = split(lines[0]).map((h) => h.toLowerCase().replace(/\s+/g, "_"));
  const rows = lines.slice(1).map((l) => {
    const cells = split(l);
    const obj = {};
    headers.forEach((h, i) => { if (FIELDS.includes(h)) obj[h] = cells[i] || ""; });
    return obj;
  }).filter((r) => r.customer_name);
  return { rows, headers };
};

export const ImportLeadsDialog = ({ open, onOpenChange, onImported }) => {
  const { isAdmin, user } = useAuth();
  const [text, setText] = useState("");
  const [rows, setRows] = useState([]);
  const [staff, setStaff] = useState([]);
  const [assignTo, setAssignTo] = useState("none");
  const [skipDup, setSkipDup] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setText(""); setRows([]); setAssignTo("none");
    api.get("/staff/assignable").then((r) => setStaff(r.data)).catch(() => {});
  }, [open]);

  useEffect(() => { setRows(parseCSV(text).rows); }, [text]);

  const readFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setText(String(reader.result));
    reader.readAsText(f);
  };

  const submit = async () => {
    if (!rows.length) return toast.error("Nothing to import yet.");
    setBusy(true);
    try {
      const { data } = await api.post("/leads/import", {
        rows, assigned_to: assignTo === "none" ? "" : assignTo, skip_duplicates: skipDup,
      });
      toast.success(`${data.created} leads imported${data.skipped ? `, ${data.skipped} skipped as duplicates` : ""}`);
      onImported?.();
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto scroll-slim">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Import leads from a file</DialogTitle>
          <DialogDescription>
            Upload a CSV exported from IndiaMART, an exhibition list or a spreadsheet. Column headers should be:{" "}
            <code className="rounded bg-muted px-1 text-[11px]">{FIELDS.join(", ")}</code>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium transition-colors hover:border-primary/40">
              <FileUp className="h-4 w-4 text-primary" /> Choose CSV file
              <input type="file" accept=".csv,text/csv" hidden onChange={readFile} data-testid="import-file" />
            </label>
            <Button type="button" variant="ghost" onClick={() => setText(SAMPLE)}>Paste sample data</Button>
          </div>

          <Field label="Or paste CSV content here">
            <Textarea rows={6} value={text} onChange={(e) => setText(e.target.value)} data-testid="import-text"
              className="font-mono text-xs" placeholder="customer_name,company,phone,email,..." />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Assign all imported leads to">
              <Select value={assignTo} onValueChange={setAssignTo}>
                <SelectTrigger data-testid="import-assign"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Leave unassigned</SelectItem>
                  {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <label className="flex items-end gap-2.5 pb-2 text-sm">
              <Checkbox checked={skipDup} onCheckedChange={(v) => setSkipDup(!!v)} />
              Skip rows whose phone number already exists
            </label>
          </div>

          {rows.length > 0 && (
            <div className="rounded-xl border border-border">
              <p className="border-b border-border bg-muted/50 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Preview . {rows.length} row{rows.length > 1 ? "s" : ""} ready
              </p>
              <div className="scroll-slim max-h-56 overflow-auto">
                <table className="w-full text-xs">
                  <tbody>
                    {rows.slice(0, 12).map((r, i) => (
                      <tr key={i} className="border-b border-border/60 last:border-0">
                        <td className="px-4 py-2 font-medium">{r.customer_name}</td>
                        <td className="px-2 py-2 text-muted-foreground">{r.company}</td>
                        <td className="num px-2 py-2 text-muted-foreground">{r.phone}</td>
                        <td className="px-2 py-2 text-muted-foreground">{r.product}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !rows.length} data-testid="import-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            Import {rows.length || ""} lead{rows.length === 1 ? "" : "s"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
